import { pan } from './viewportState.js';
import { VALUE_NODE_TYPES } from '../graph/nodeDefs.js';
import { nodeDefsForKind, type GraphRegistryKind } from '../graph/nodeRegistry.js';
import type { AppState } from '../state/appState.js';
import type { NodeEditorController } from './nodeEditor.js';

interface ContextMenuNodeEntry {
  type: string;
  label: string;
  graphs: GraphRegistryKind[];
  cat: string;
  subcat?: string;
}

export function contextMenuNodeEntries(): ContextMenuNodeEntry[] {
  const entries = new Map<string, ContextMenuNodeEntry>();
  const graphKinds: GraphRegistryKind[] = ['geometry', 'material', 'data', 'event', 'query'];
  for (const graph of graphKinds) {
    for (const [type, def] of Object.entries(nodeDefsForKind(graph))) {
      const declared = 'graphs' in def && Array.isArray(def.graphs) ? def.graphs as string[] : ['geometry', 'material'];
      if (!declared.includes(graph)) continue;
      for (const cat of Array.isArray(def.cat) ? def.cat : [def.cat || 'Other']) {
        const key = `${type}\u0000${def.label}\u0000${cat}\u0000${def.subcat ?? ''}`;
        const entry = entries.get(key) ?? { type, label: def.label, graphs: [], cat, subcat: def.subcat };
        if (!entry.graphs.includes(graph)) entry.graphs.push(graph);
        entries.set(key, entry);
      }
    }
  }
  return [...entries.values()];
}

export function initContextMenu(
  graphArea: HTMLElement,
  deps: {
    appState: AppState;
    nodeEditor: Pick<NodeEditorController, 'createNode'>;
    scheduleRender: () => void;
    updateParams: () => void;
  },
): void {
  const contextMenu = document.getElementById('context-menu')!;
  let contextMenuPos = { x: 0, y: 0 };
  const isEmptyGraphSpace = (target: EventTarget | null): boolean => {
    if (!(target instanceof Element)) return false;
    return target === graphArea
      || target.id === 'graph-canvas-wrapper'
      || target.id === 'conn-svg';
  };

  contextMenu.innerHTML = '';
  const categories: Record<string, { type: string; label: string; graphs: string[]; subcat?: string }[]> = {};
  const CATEGORY_LABELS: Record<string, string> = {
    source: 'Source',
    math: 'Math',
    vector: 'Vector',
    bounds: 'Bounds',
    transform: 'Transform',
    sdf: 'SDF',
    special: 'Special',
    output: 'Output',
    event: 'Events',
    control: 'Control',
    command: 'Commands',
    logic: 'Logic',
    Other: 'Other',
  };

  for (const { cat, type, label, graphs, subcat } of contextMenuNodeEntries()) {
    if (!categories[cat]) categories[cat] = [];
    categories[cat].push({ type, label, graphs, subcat });
  }
  const SUBCAT_LABELS: Record<string, string> = {
    shapes: 'Shapes',
    boolean: 'Boolean',
    'smooth-boolean': 'Smooth boolean',
    glyph: 'Glyph',
    arithmetic: 'Arithmetic',
    trigonometry: 'Trigonometry',
    range: 'Range',
    blend: 'Blend',
    value: 'Value',
  };

  function makeButton(type: string, label: string, graphs: string[]): HTMLButtonElement {
    const btn = document.createElement('button');
    btn.className = 'ui-btn ui-btn--tile ui-btn--compact';
    btn.dataset.type = type;
    btn.dataset.graphs = graphs.join(',');
    btn.textContent = label;
    return btn;
  }

  for (const [cat, nodes] of Object.entries(categories)) {
    const section = document.createElement('div');
    section.className = 'context-menu-section';

    const title = document.createElement('div');
    title.className = 'context-menu-title';
    title.textContent = CATEGORY_LABELS[cat] ?? cat;
    section.appendChild(title);

    const hasSubcats = nodes.some(n => n.subcat);

    if (!hasSubcats) {
      const grid = document.createElement('div');
      grid.className = 'context-menu-grid';
      for (const { type, label, graphs } of nodes) {
        grid.appendChild(makeButton(type, label, graphs));
      }
      section.appendChild(grid);
    } else {
      const subcatOrder: string[] = [];
      const subcatMap = new Map<string, typeof nodes>();
      for (const entry of nodes) {
        const key = entry.subcat ?? '';
        if (!subcatMap.has(key)) { subcatOrder.push(key); subcatMap.set(key, []); }
        subcatMap.get(key)!.push(entry);
      }
      for (const key of subcatOrder) {
        const group = document.createElement('div');
        group.className = 'context-menu-subgroup';
        if (key) {
          const subheader = document.createElement('div');
          subheader.className = 'context-menu-subheader';
          subheader.textContent = SUBCAT_LABELS[key] ?? key;
          group.appendChild(subheader);
        }
        const grid = document.createElement('div');
        grid.className = 'context-menu-grid';
        for (const { type, label, graphs } of subcatMap.get(key)!) {
          grid.appendChild(makeButton(type, label, graphs));
        }
        group.appendChild(grid);
        section.appendChild(group);
      }
    }

    contextMenu.appendChild(section);
  }

  graphArea.addEventListener('contextmenu', e => {
    if (!isEmptyGraphSpace(e.target)) {
      contextMenu.style.display = 'none';
      e.preventDefault();
      return;
    }
    e.preventDefault();
    const rect = graphArea.getBoundingClientRect();
    contextMenuPos.x = e.clientX - rect.left;
    contextMenuPos.y = e.clientY - rect.top;

    const appState = deps.appState;
    const slot = appState.editorGraphKind;
    // Show/hide buttons based on current slot
    contextMenu.querySelectorAll<HTMLElement>('.ui-btn[data-graphs]').forEach(btn => {
      const allowed = btn.dataset.graphs!.split(',');
      btn.style.display = allowed.includes(slot) ? '' : 'none';
    });
    // Hide empty subgroups
    contextMenu.querySelectorAll<HTMLElement>('.context-menu-subgroup').forEach(group => {
      const visibleBtns = group.querySelectorAll<HTMLElement>('.ui-btn:not([style*="display: none"])');
      group.style.display = visibleBtns.length > 0 ? '' : 'none';
    });
    // Hide empty sections (all buttons hidden)
    contextMenu.querySelectorAll<HTMLElement>('.context-menu-section').forEach(section => {
      const visibleBtns = section.querySelectorAll<HTMLElement>('.ui-btn:not([style*="display: none"])');
      section.style.display = visibleBtns.length > 0 ? '' : 'none';
    });

    const outBtn = contextMenu.querySelector('.ui-btn[data-type="Output"]') as HTMLButtonElement | null;
    if (outBtn) {
      const hasOut = (appState.editorGraphKind === 'geometry' || appState.editorGraphKind === 'material')
        && appState.nodes.some(node => node.type === 'Output');
      outBtn.disabled = hasOut;
      outBtn.style.opacity = hasOut ? '0.5' : '1';
      outBtn.style.cursor = hasOut ? 'not-allowed' : 'pointer';
    }

    const GAP = 4;
    const belowMid = e.clientY > window.innerHeight / 2;
    contextMenu.style.maxHeight = belowMid
      ? `${e.clientY - GAP}px`
      : `${window.innerHeight - e.clientY - GAP}px`;
    contextMenu.style.display = 'block';
    contextMenu.scrollTop = belowMid ? contextMenu.scrollHeight : 0;
    const left = e.clientX;
    const top = belowMid
      ? Math.max(GAP, e.clientY - contextMenu.offsetHeight)
      : e.clientY;
    contextMenu.style.left = `${left}px`;
    contextMenu.style.top = `${top}px`;
  });

  document.addEventListener('mousedown', e => {
    if (contextMenu.style.display !== 'none' && !contextMenu.contains(e.target as HTMLElement)) {
      contextMenu.style.display = 'none';
    }
  });

  contextMenu.addEventListener('click', e => {
    const btn = (e.target as HTMLElement).closest('.ui-btn[data-type]') as HTMLButtonElement | null;
    if (!btn || btn.disabled) return;
    const type = btn.dataset.type!;

    const cx = (contextMenuPos.x - pan.x) / pan.scale;
    const cy = (contextMenuPos.y - pan.y) / pan.scale;
    const newNode = deps.nodeEditor.createNode(type, cx, cy);
    if (!newNode) return;

    if (VALUE_NODE_TYPES.has(type)) deps.updateParams();
    deps.scheduleRender();
    contextMenu.style.display = 'none';
  });
}
